import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";

const scopes = [
  {
    icon: "ri-film-line",
    title: "影视作品资料",
    description: "收录电影与电视剧的作品信息、角色、导演、剧情简介与剧照，可通过类型、年份与角色类型筛选。",
    to: "/works",
    label: "浏览作品库",
  },
  {
    icon: "ri-newspaper-line",
    title: "动态资讯整理",
    description: "汇总作品公告、媒体访谈与活动报道，按发布时间倒序排列，并保留原文要点与配图说明。",
    to: "/news",
    label: "查看动态",
  },
  {
    icon: "ri-user-3-line",
    title: "演员资料档案",
    description: "包含基本资料、职业经历年表、代表作品与荣誉记录，方便影迷与研究者快速查阅。",
    to: "/profile",
    label: "查看资料",
  },
];

const sections = [
  {
    id: "positioning",
    title: "站点定位",
    paragraphs: [
      "翔田千里影视资料站是一个以演员作品为核心的资料型网站。我们不生产新闻，也不提供任何影像内容，只把公开信息整理成可检索、可追溯的结构：一部作品对应一张卡片，一条资讯对应一个独立页面。",
      "站点面向三类读者：希望快速了解她作品的普通影迷、需要核对作品年表的影视研究者，以及正在寻找特定题材作品的观众。因此所有列表都提供筛选与排序，所有详情都保留可回溯的分类标签。",
    ],
  },
  {
    id: "scope",
    title: "内容范围与更新",
    paragraphs: [
      "目前收录 2015 至 2024 年间的 14 部影视作品与 9 条动态资讯，覆盖电影、电视剧、访谈、活动报道与出版信息。作品条目包含上映年份、类型、导演、角色、片长与剧情简介。",
      "网站保持平均每月一次的整理频率。新增内容会同时出现在首页最新动态与对应列表页；已发布条目如有修订，会在资讯正文中说明。",
    ],
  },
  {
    id: "usage",
    title: "使用说明",
    paragraphs: [
      "浏览作品：进入「影视作品」后可组合使用类型、年份、角色类型三个筛选条件，并切换按上映时间或热度排序。结果为空时可一键重置。",
      "全站搜索：顶部搜索框会同时检索作品名称、英文名、类型标签、角色、导演与资讯标题、摘要、标签。搜索结果页可切换「全部 / 作品 / 资讯」查看。",
      "图片预览：作品详情页的剧照与海报支持点击放大，可使用左右方向键切换、Esc 键关闭。",
      "分享：详情页的分享按钮为前端演示，功能为复制当前页面链接，不会向任何第三方平台发送数据。",
    ],
  },
  {
    id: "privacy",
    title: "隐私说明",
    paragraphs: [
      "本站为纯前端演示站点，不设置账号系统，不收集姓名、手机号、邮箱等个人信息，也不使用第三方统计脚本或广告追踪。",
      "页面中的搜索关键词与筛选条件仅保存在当前浏览器地址中，不会上传到服务器。关闭页面后不会留存任何记录。",
      "站内所有图片均为示意性插画素材，人物形象非真实肖像；作品名称、剧情与资讯内容为演示文本，不代表真实作品信息。",
    ],
  },
  {
    id: "copyright",
    title: "版权声明",
    paragraphs: [
      "本站为影视资料整理与展示用途的演示站点，不提供影片播放、下载、在线观看或任何形式的影像传播服务，也不涉及付费内容。",
      "站内文字内容由本站整理撰写，配图为示意素材。如权利人认为内容存在不当使用，可通过下方联系方式与我们沟通，我们会在核实后及时处理。",
    ],
  },
];

export default function About() {
  usePageMeta({
    title: "关于翔田千里－站点定位、内容范围与使用说明",
    description:
      "关于翔田千里影视资料站：说明站点定位、内容范围、更新频率、使用方式、版权声明与隐私说明。本站为资料整理型演示站点，不提供播放与下载服务。",
    keywords: "关于翔田千里,站点说明,版权声明,隐私政策,使用说明",
  });

  return (
    <SiteLayout>
      <section className="border-b border-background-200/70 bg-background-50">
        <div className="container-page py-9 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于" }]} />
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-px w-7 bg-accent-500"></span>
                <span className="text-[11px] uppercase tracking-wider2 text-accent-600">About This Site</span>
              </div>
              <h1 className="mt-3 font-heading text-3xl leading-tight text-foreground-950 md:text-[40px]">
                关于翔田千里
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600">
                一个以作品为核心的演员资料站。整理翔田千里的影视作品、动态资讯与职业经历，把散落的公开信息做成可以逐层翻阅的档案。
              </p>
            </div>
            <div className="rounded-lg border border-background-200/80 bg-background-100 p-5">
              <p className="text-[11px] uppercase tracking-wider2 text-foreground-400">建站信息</p>
              <dl className="mt-3 space-y-2 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">站点类型</dt>
                  <dd className="text-foreground-900">演员资料 / 作品索引</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">内容语言</dt>
                  <dd className="text-foreground-900">简体中文</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">数据方式</dt>
                  <dd className="text-foreground-900">前端演示数据</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">更新频率</dt>
                  <dd className="text-foreground-900">每月整理</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-10 md:py-14">
        <div className="container-page grid grid-cols-1 gap-8 lg:grid-cols-[220px_1fr] lg:gap-12">
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <p className="text-[11px] uppercase tracking-wider2 text-foreground-400">页面目录</p>
            <nav className="mt-3 flex flex-wrap gap-2 lg:flex-col lg:gap-1" aria-label="页面目录">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="rounded-md px-3 py-2 text-xs text-foreground-600 transition-colors hover:bg-background-50 hover:text-primary-700"
                >
                  {section.title}
                </a>
              ))}
            </nav>
            <div className="mt-5 hidden rounded-lg bg-primary-950 p-4 lg:block">
              <p className="font-heading text-sm text-background-50">内容导航</p>
              <p className="mt-2 text-[11px] leading-relaxed text-background-50/60">
                想直接看作品？可以从作品库开始。
              </p>
              <Link
                to="/works"
                className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-3 py-1.5 text-[11px] text-primary-950"
              >
                前往作品库
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </aside>

          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {scopes.map((scope) => (
                <div key={scope.title} className="flex h-full flex-col rounded-lg border border-background-200/80 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-800">
                    <i className={scope.icon} aria-hidden="true"></i>
                  </span>
                  <h2 className="mt-3 font-heading text-base text-foreground-950">{scope.title}</h2>
                  <p className="mt-2 flex-1 text-xs leading-relaxed text-foreground-600">{scope.description}</p>
                  <Link
                    to={scope.to}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary-700 transition-colors hover:text-accent-700"
                  >
                    {scope.label}
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </Link>
                </div>
              ))}
            </div>

            {sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className="scroll-mt-28 rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-7"
              >
                <h2 className="font-heading text-xl text-foreground-950">{section.title}</h2>
                <div className="mt-3 space-y-3">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 12)} className="text-sm leading-loose text-foreground-700">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}

            <section className="rounded-lg bg-primary-950 p-5 text-background-50 md:p-7">
              <h2 className="font-heading text-xl">联系方式</h2>
              <p className="mt-3 text-sm leading-relaxed text-background-50/70">
                内容更正、资料补充或版权沟通，可通过以下方式联系我们。本站不设客服系统，回复时间以工作日为准。
              </p>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-md border border-background-50/20 bg-background-50/5 p-4">
                  <p className="text-[11px] text-background-50/50">内容与资料</p>
                  <p className="mt-1.5 text-sm">archive@chisato-shoda.example</p>
                </div>
                <div className="rounded-md border border-background-50/20 bg-background-50/5 p-4">
                  <p className="text-[11px] text-background-50/50">版权与授权</p>
                  <p className="mt-1.5 text-sm">rights@chisato-shoda.example</p>
                </div>
              </div>
              <p className="mt-4 text-[11px] text-background-50/40">
                以上邮箱为演示信息，不接收真实邮件。
              </p>
            </section>

            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-5">
              <p className="text-xs leading-relaxed text-foreground-500">
                声明：本网站为影视资料整理与展示的演示项目，所有作品名称、剧情、资讯与荣誉均为示例内容，不对应真实作品；站内人物图片为示意素材，非真实肖像。本站不提供影片播放、下载或付费服务。
              </p>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}